import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Collapse, Fade } from '@mui/material';
import styled from 'styled-components';
import { joiResolver } from '@hookform/resolvers/joi';
import { useDispatch } from 'react-redux';
import TextField from '../components/common/TextField';
import PasswordField from '../components/common/PasswordField';
import SEO from '../components/common/SEO';
import Button from '../components/common/Button';
import TextLink from '../components/common/TextLink';
import { loginSchema } from '../validation/auth-schema';
import { login } from '../services/auth.service';
import { hasAnyRoles } from '../lib/role';
import AnonymousLayout from '../components/AnonymousLayout';
import withoutAuth from '../hoc/withoutAuth';
import { useToast } from '../context/ToastContext';
import { SITE_DESCRIPTION } from '../config/seo.constants';
import { setUser } from '../store/auth/actions';
import regionConfig from '../config';
import { generateDeviceId } from 'src/lib/generateDeviceId';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FEATURE_FLAG } from 'src/config/common.constants';
import Recaptcha from 'react-google-recaptcha';

const StyledLoginPage = styled.div`
    width: 100%;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 8rem;

    @media (max-width: 768px) {
        height: auto;
        margin-bottom: 105px;
    }

    .signup-hint-text {
        color: white;
    }

    .signup-link {
        margin-left: 5px;
        color: rgb(9, 148, 76);
        cursor: pointer;
    }
    .terms {
        // margin-left: 5px;
        display: flex;
        justify-content: center;
        color: rgb(9, 148, 76);
        font-size: 12px;
        margin-top: 10px;
    }

    .create-account-btn {
        color: #0a5;
        font-weight: 600;
        font-size: 20px;
        border: none;
        font-family: 'Source Sans Pro' !important;
        &:hover {
            background-color: rgba(0, 0, 0, 0.04);
        }
    }
    .login-btn {
        font-family: 'Source Sans Pro';
        border-color: #0a5;
    }
    .need-help {
        display: flex;
        justify-content: center;
        margin-top: 1rem;
    }
    .captcha-container {
        display: flex;
        justify-content: center;
        margin: 0 0 1rem 0;
    }
`;

const LoginPage = () => {
    const toast = useToast();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // const user = useUser();

    const { isEnabled } = useFeatureFlag(FEATURE_FLAG.CUSTOMER_SUPPORT_ENABLED);

    const [captchaVerified, setCaptchaVerified] = useState(null);
    const [showCaptcha, setShowCaptcha] = useState(false);

    const captcha = useRef(null);

    const { register, handleSubmit, errors } = useForm({
        defaultValues: {
            email: '',
            password: '',
        },
        mode: 'onSubmit',
        resolver: joiResolver(loginSchema),
    });

    const onSubmit = handleSubmit(async (credentials) => {
        if (showCaptcha && !captchaVerified) {
            toast.error('Please verify that you are a human');

            return;
        }
        const deviceid = generateDeviceId();
        const { data, error, isCaptchaRequired } = await login({
            ...credentials,
            deviceid,
            recaptchaToken: captchaVerified,
        });

        if (data) {
            if (hasAnyRoles(data.user.roles, ['ROLE_USER', 'ROLE_SL_GUEST'])) {
                dispatch(setUser(data.user));
            } else {
                toast.error('User not allowed');
            }
        } else if (error) {
            setShowCaptcha(!!isCaptchaRequired);
            toast.error(error);
            //@ts-ignore
            captcha.current?.reset();
            setCaptchaVerified(null);
        }
    });

    const handleCreateAccount = () => {
        if (regionConfig.isUK) {
            navigate('/guestuserregistration');
        } else {
            window.open(regionConfig.accountInterestUrl, '_self');
        }
    };

    const handleNeedHelpClick = () => {
        const URL = regionConfig.needHelpUrl;
        window.open(URL, '_blank');
    };

    const handleCaptchaVerified = (response: any) => {
        if (response) {
            setCaptchaVerified(response);
        } else {
            setCaptchaVerified(null);
        }
    };

    return (
        <AnonymousLayout isLoginScreen>
            <StyledLoginPage>
                <SEO
                    title="Login"
                    description={`${regionConfig.appName} Login -${SITE_DESCRIPTION}`}
                />

                {/* <div className="flex"> */}
                <Card className="card">
                    <CardContent>
                        <form noValidate name="login-form" onSubmit={onSubmit}>
                            <TextField
                                label="Email"
                                name="email"
                                aria-required="true"
                                inputRef={register}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                placeholder="Enter your email"
                            />
                            <div className="flex justify-end -mb-6">
                                <Link to="/forgotPassword">
                                    <TextLink
                                        href="forgot-password.html"
                                        aria-label="Forgot Password"
                                    >
                                        Forgot password?
                                    </TextLink>
                                </Link>
                            </div>
                            <PasswordField
                                allowPasswordToggle
                                label="Password"
                                name="password"
                                aria-required="true"
                                inputRef={register}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                placeholder="Enter your password"
                                ariaLabel="Password"
                            />
                            <Collapse in={showCaptcha} timeout={400}>
                                <Fade
                                    in={showCaptcha}
                                    timeout={400}
                                    style={{ transitionDelay: showCaptcha ? '150ms' : '0ms' }}
                                >
                                    <Box className="captcha-container">
                                        <Recaptcha
                                            sitekey={regionConfig.captchaSiteKey}
                                            ref={captcha}
                                            render="explicit"
                                            onChange={handleCaptchaVerified}
                                        />
                                    </Box>
                                </Fade>
                            </Collapse>
                            <Button
                                type="submit"
                                fullWidth
                                size="large"
                                className="primary-bg-color normal-case login-btn"
                                aria-label="Login"
                            >
                                Login
                            </Button>

                            <Button
                                onClick={handleCreateAccount}
                                variant="text"
                                fullWidth
                                size="large"
                                className="mt-5 normal-case create-account-btn"
                                aria-label="create-account"
                            >
                                Create a new account
                            </Button>
                            {isEnabled === true && (
                                <div className="need-help">
                                    <TextLink
                                        onClick={() => {
                                            handleNeedHelpClick();
                                        }}
                                        aria-label="Need help"
                                    >
                                        Need help?
                                    </TextLink>
                                </div>
                            )}
                        </form>
                    </CardContent>
                </Card>
                {/* </div> */}
            </StyledLoginPage>
        </AnonymousLayout>
    );
};

export default withoutAuth(LoginPage);
